import { Suspense, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { startSession } from "@react-three/xr";
import UI from "../UI";
import Scene from "../webgl/Scene";
import ShowroomCanvas from "./ShowroomCanvas";
import Loading from "../../util/Loading";
import useTransitionStore from "../../state/transitions";
import useCustomizerStore from "../../state/customizer";
import {
  fetchCustomizerData,
  getSelectedWheelIndex,
} from "../../util/customizerData";
import { defaultControlAngles } from "../../util/defaultControlAngles";
import {
  getPreferredImmersiveSessionMode,
  getXrEnterLabel,
} from "../../util/xrSessionSupport";

export default function OriginalExperiencePage() {
  const orbitModeActive = useTransitionStore((state) => state.OrbitModeActive);
  const displayMode = useTransitionStore((state) => state.DisplayMode);
  const model = useCustomizerStore((state) => state.model);
  const setAllTrimColors = useCustomizerStore((state) => state.setAllTrimColors);
  const trimData = useMemo(() => fetchCustomizerData(), []);
  const trimLabel = trimData[model].label;
  const [vrActive, setVrActive] = useState(false);
  const [xrReady, setXrReady] = useState(false);
  const [vrPending, setVrPending] = useState(false);
  const [xrError, setXrError] = useState(null);
  const [sessionMode, setSessionMode] = useState(null);
  const [mainSceneEpoch, setMainSceneEpoch] = useState(0);
  const setOrbitMode = useTransitionStore((state) => state.setOrbitMode);
  const setDisplayMode = useTransitionStore((state) => state.setDisplayMode);
  const setDayMode = useTransitionStore((state) => state.setDayMode);
  const setHeaderFixed = useTransitionStore((state) => state.setHeaderFixed);
  const setModelInHeader = useTransitionStore((state) => state.setModelInHeader);
  const setCamIndex = useTransitionStore((state) => state.setCamIndex);
  const setShowCar = useTransitionStore((state) => state.setShowCar);
  const setShowInterior = useTransitionStore((state) => state.setShowInterior);
  const setShowHotspots = useTransitionStore((state) => state.setShowHotspots);
  const setCameraSide = useTransitionStore((state) => state.setCameraSide);
  const setHotspotControls = useTransitionStore((state) => state.setHotspotControls);
  const setHotspotZoomed = useTransitionStore((state) => state.setHotspotZoomed);
  const setSuspensionMode = useTransitionStore((state) => state.setSuspensionMode);
  const setWheelIndex = useTransitionStore((state) => state.setWheelIndex);
  const setAzimuthAndPolarAngles = useTransitionStore(
    (state) => state.setAzimuthAndPolarAngles
  );

  const restoreMainSceneAfterVr = () => {
    const data = fetchCustomizerData();

    setAllTrimColors(
      data[0].colors[data[0].exterior.selectedColor ?? 0],
      data[1].colors[data[1].exterior.selectedColor ?? 0],
      data[2].colors[data[2].exterior.selectedColor ?? 0]
    );
    setWheelIndex(getSelectedWheelIndex(model));
    setOrbitMode(true);
    setDisplayMode("exterior");
    setDayMode("day");
    setHeaderFixed(true);
    setModelInHeader(true);
    setCamIndex(1);
    setShowCar(true);
    setShowInterior(false);
    setShowHotspots(false);
    setCameraSide("passenger");
    setHotspotControls(false);
    setHotspotZoomed(false);
    setSuspensionMode(false);
    setAzimuthAndPolarAngles(defaultControlAngles);
    setMainSceneEpoch((current) => current + 1);
  };

  useEffect(() => {
    let cancelled = false;

    const detectSessionMode = async () => {
      const supportedMode = await getPreferredImmersiveSessionMode(navigator?.xr);

      if (!cancelled) {
        setSessionMode(supportedMode);
      }
    };

    detectSessionMode();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!orbitModeActive && !vrActive) {
      setXrReady(false);
      setVrPending(false);
    }
  }, [orbitModeActive, vrActive]);

  const handleEnterVr = async () => {
    try {
      if (!xrReady || vrPending) {
        return;
      }

      const resolvedMode =
        sessionMode ??
        (await getPreferredImmersiveSessionMode(navigator?.xr));

      if (!resolvedMode) {
        setXrError("This browser cannot start immersive XR. Open this experience in an XR-capable browser on your headset.");
        return;
      }

      setXrError(null);
      setVrPending(true);

      const session = await startSession(resolvedMode, {
        optionalFeatures: ["local-floor", "bounded-floor", "hand-tracking", "light-estimation", "plane-detection", "hit-test"],
      });

      if (session) {
        setVrActive(true);
        setVrPending(false);
        session.addEventListener(
          "end",
          () => {
            restoreMainSceneAfterVr();
            setVrActive(false);
          },
          { once: true }
        );
        return;
      }

      restoreMainSceneAfterVr();
      setVrPending(false);
    } catch (error) {
      console.error("Toyota XR entry failed", error);
      restoreMainSceneAfterVr();
      setVrPending(false);
      setXrError("XR could not start. Check browser permissions, then select Enter XR to retry.");
    }
  };

  return (
    <Suspense fallback={<Loading />}>
      <PageRoot>
        {!vrActive && (
          <>
            <UI />
            <Scene key={mainSceneEpoch} />
          </>
        )}
        {orbitModeActive && (
          <XrHost aria-hidden="true" $active={vrPending || vrActive}>
            <ShowroomCanvas
              hideOverlay
              trimLabel={trimLabel}
              onCanvasReady={() => setXrReady(true)}
              renderSceneContent={vrPending || vrActive}
            />
          </XrHost>
        )}

        {xrError && !vrActive && <p role="alert" style={{ position: "absolute", bottom: 96, right: 24, zIndex: 30, background: "#000", color: "#fff", padding: 16, maxWidth: 320 }}>{xrError}</p>}
        {orbitModeActive && !vrActive && (
          <EnterVrButton
            type="button"
            onClick={handleEnterVr}
            aria-label="Enter the current environment in XR"
            disabled={vrPending || !xrReady}
          >
            {vrPending ? "Entering XR..." : getXrEnterLabel(sessionMode)}
          </EnterVrButton>
        )}
      </PageRoot>
    </Suspense>
  );
}

const PageRoot = styled.main`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #000;
`;

const XrHost = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10950;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? "visible" : "hidden")};
  pointer-events: none;
  overflow: hidden;
  background: #000;
  transition: opacity 180ms ease;
`;

const EnterVrButton = styled.button`
  position: fixed;
  top: calc((var(--app-header-height, 104px) - 36px) / 2);
  left: 50%;
  transform: translateX(-50%);
  z-index: 11000;
  min-width: 120px;
  height: 36px;
  border: none;
  border-radius: 999px;
  background: #ffffff;
  color: #000000;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.28);

  &:disabled {
    opacity: 0.7;
    cursor: progress;
  }

  @media (max-width: 768px) {
    min-width: 108px;
    height: 32px;
    font-size: 12px;
    top: calc((var(--app-header-height, 60px) - 32px) / 2);
  }
`;
